<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white overflow-hidden py-20 md:py-32">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-blue-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-400 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-blue-300">
            <i class="bx bx-shield-check mr-2"></i>Business Continuity
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Backup As A <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Service</span>
        </h1>
        
        <p class="text-xl text-blue-100 mb-8 max-w-2xl mx-auto lg:mx-0">
          Comprehensive backup solutions powered by Veeam Backup Agent with automated protection, rapid restoration, and business continuity planning. Ensure your critical data is always safe and recoverable with our managed backup service.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto lg:mx-0">
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">99.9%</div>
            <div class="text-sm text-blue-200">Data Integrity</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">RTO < 4</div>
            <div class="text-sm text-blue-200">Hours</div>
          </div>
          <div class="bg-cyan-500/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/30">
            <div class="text-3xl font-bold text-cyan-400 mb-1">Powered by</div>
            <div class="text-sm text-blue-200">Veeam</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/backup-recovery-hero.png" alt="Backup As A Service" class="w-full h-auto object-cover">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-sync text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Automated</div>
              <div class="text-xs">Backup</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-reset text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Instant</div>
              <div class="text-xs">Recovery</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- 3-2-1 Backup Strategy Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Veeam Powered
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Industry-Proven <span class="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">3-2-1 Backup Strategy</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Follow industry best practices with our proven 3-2-1 backup strategy powered by Veeam Backup Agent for maximum data protection and business continuity.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
      <!-- Three Copies of Data -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-cyan-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <div class="text-2xl font-bold text-white">3</div>
          </div>
          <span class="bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full border border-cyan-500/40">Copies</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Three Copies of Data</h3>
        <p class="text-slate-300 mb-4">
          Maintain three copies of your critical data - one primary and two backup copies for maximum protection against data loss and corruption.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Primary production data</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Local backup copy</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Secondary backup copy</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Automatic synchronization</span>
          </li>
        </ul>
        <div class="flex items-center text-sm text-cyan-200 font-semibold bg-cyan-500/10 border border-cyan-500/40 p-3 rounded-lg">
          <i class="bx bx-shield-quarter mr-2"></i>
          <span>99.99% Data Integrity</span>
        </div>
      </div>

      <!-- Different Media Types -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <div class="text-2xl font-bold text-white">2</div>
          </div>
          <span class="bg-blue-500/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-500/40">Media</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Different Media Types</h3>
        <p class="text-slate-300 mb-4">
          Store data on two different types of media to avoid single points of failure and ensure redundancy across storage platforms.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Local disk storage</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Cloud storage systems</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Tape backup media</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Network attached storage</span>
          </li>
        </ul>
        <div class="flex items-center text-sm text-blue-200 font-semibold bg-blue-500/10 border border-blue-500/40 p-3 rounded-lg">
          <i class="bx bx-repost mr-2"></i>
          <span>Zero Downtime Recovery</span>
        </div>
      </div>

      <!-- One Offsite Copy -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-indigo-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <div class="text-2xl font-bold text-white">1</div>
          </div>
          <span class="bg-indigo-500/20 text-indigo-200 text-xs font-bold px-3 py-1 rounded-full border border-indigo-500/40">Offsite</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">One Offsite Copy</h3>
        <p class="text-slate-300 mb-4">
          Keep one copy offsite or in the cloud for protection against local disasters, natural calamities, and site-specific incidents.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Geographically separated</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Cloud-based storage</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Secure data centers</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check text-cyan-600 mr-2"></i>
            <span class="text-sm">Remote site replication</span>
          </li>
        </ul>
        <div class="flex items-center text-sm text-indigo-200 font-semibold bg-indigo-500/10 border border-indigo-500/40 p-3 rounded-lg">
          <i class="bx bx-globe mr-2"></i>
          <span>Global Accessibility</span>
        </div>
      </div>
    </div>
    
    <!-- Strategy Benefits Section -->
    <div class="text-center mb-12">
      <h3 class="text-2xl md:text-3xl font-bold text-white mb-4">Additional Benefits</h3>
      <p class="text-lg text-slate-300 max-w-2xl mx-auto">Why our 3-2-1 backup strategy provides superior protection</p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <!-- Benefit 1 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-blue-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-shield-alt text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">Ransomware Protection</h4>
        <p class="text-slate-300">Isolate clean backups from infected systems with air-gapped storage solutions.</p>
      </div>
      
      <!-- Benefit 2 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-cyan-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-time-five text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">Fast Recovery</h4>
        <p class="text-slate-300">Restore critical systems in minutes, not hours, with instant recovery capabilities.</p>
      </div>
      
      <!-- Benefit 3 -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 text-center border border-purple-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <i class="bx bx-file-find text-3xl text-white"></i>
        </div>
        <h4 class="text-xl font-bold text-white mb-3">Compliance Ready</h4>
        <p class="text-slate-300">Meet regulatory requirements with audit-ready backup documentation and reporting.</p>
      </div>
    </div>
  </div>
</section>

<!-- Enterprise-Grade Backup Features Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-500/10 border border-indigo-500/40 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Veeam Agent Features
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Enterprise-Grade <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Backup Features</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Advanced backup and recovery capabilities powered by Veeam Backup Agent designed for enterprise environments with high availability and compliance requirements.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
      <!-- Continuous Data Protection -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-infinite text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-blue-500/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-500/40">Real-time</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Continuous Data Protection</h3>
        <p class="text-slate-300 mb-4">
          Real-time backup with continuous data protection ensuring minimal data loss with RPO approaching zero.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Real-time replication</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Journal-based recovery</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Point-in-time snapshots</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Instant rollback capability</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Availability:</div>
            <div class="font-semibold text-white">99.99%</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">RPO:</div>
            <div class="font-semibold text-white">&lt;1min</div>
          </div>
        </div>
      </div>

      <!-- Intelligent Deduplication -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-cyan-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-chip text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-cyan-500/20 text-cyan-200 text-xs font-bold px-3 py-1 rounded-full border border-cyan-500/40">AI-Powered</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Intelligent Deduplication</h3>
        <p class="text-slate-300 mb-4">
          AI-powered deduplication technology reducing backup storage requirements by up to 95% while maintaining performance.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Global deduplication</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Variable-length blocks</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Intelligent compression</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Storage optimization</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Reduction:</div>
            <div class="font-semibold text-white">95%</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Speed:</div>
            <div class="font-semibold text-white">4x Faster</div>
          </div>
        </div>
      </div>

      <!-- Advanced Encryption -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-purple-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-lock-alt text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full border border-purple-500/40">Security</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Advanced Encryption</h3>
        <p class="text-slate-300 mb-4">
          Enterprise-grade encryption both in transit and at rest with advanced key management and compliance features.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">AES-256 encryption</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">End-to-end security</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Key management system</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Compliance reporting</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Encryption:</div>
            <div class="font-semibold text-white">AES-256</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Compliance:</div>
            <div class="font-semibold text-white">FIPS 140-2</div>
          </div>
        </div>
      </div>

      <!-- Instant Recovery -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-emerald-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-bolt-circle text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-emerald-500/20 text-emerald-200 text-xs font-bold px-3 py-1 rounded-full border border-emerald-500/40">Instant</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Instant Recovery</h3>
        <p class="text-slate-300 mb-4">
          Instant VM and file recovery capabilities allowing immediate access to backup data without lengthy restoration processes.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Instant VM boot</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Instant file access</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Storage vMotion</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Live migration support</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Recovery:</div>
            <div class="font-semibold text-white">&lt;30s</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Success:</div>
            <div class="font-semibold text-white">100%</div>
          </div>
        </div>
      </div>

      <!-- Backup Analytics -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-amber-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-bar-chart-alt text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-amber-500/20 text-amber-200 text-xs font-bold px-3 py-1 rounded-full border border-amber-500/40">Monitoring</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Backup Analytics</h3>
        <p class="text-slate-300 mb-4">
          Comprehensive backup monitoring and analytics with predictive insights and performance optimization recommendations.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Real-time monitoring</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Performance analytics</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Predictive alerts</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Custom dashboards</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Monitoring:</div>
            <div class="font-semibold text-white">24/7</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Powered:</div>
            <div class="font-semibold text-white">AI</div>
          </div>
        </div>
      </div>

      <!-- Compliance Ready -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-rose-500/40">
        <div class="w-16 h-16 bg-gradient-to-br from-rose-600 to-pink-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-file-blank text-3xl text-white"></i>
        </div>
        <div class="mb-3">
          <span class="bg-rose-500/20 text-rose-200 text-xs font-bold px-3 py-1 rounded-full border border-rose-500/40">Compliance</span>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Compliance Ready</h3>
        <p class="text-slate-300 mb-4">
          Built-in compliance features for GDPR, HIPAA, SOX, and other regulatory requirements with automated reporting.
        </p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">GDPR compliance</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">HIPAA support</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">SOX reporting</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span class="text-sm">Audit trails</span>
          </li>
        </ul>
        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
          <div>
            <div class="text-sm text-slate-400">Standards:</div>
            <div class="font-semibold text-white">20+</div>
          </div>
          <div>
            <div class="text-sm text-slate-400">Reports:</div>
            <div class="font-semibold text-white">Automated</div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Enterprise-Grade Protection Highlight -->
    <div class="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
      <div class="flex flex-col md:flex-row items-center">
        <div class="w-16 h-16 bg-slate-800/30 rounded-xl flex items-center justify-center mb-4 md:mb-0 md:mr-6">
          <i class="bx bx-trophy text-3xl"></i>
        </div>
        <div class="text-center md:text-left flex-1">
          <h3 class="text-2xl font-bold mb-2">Enterprise-Grade Protection</h3>
          <p class="text-blue-100 mb-4">All features work seamlessly together to provide comprehensive data protection with industry-leading 99.99% availability and instant recovery capabilities.</p>
        </div>
        <div class="grid grid-cols-2 gap-6">
          <div class="text-center">
            <div class="text-3xl font-bold">99.99%</div>
            <div class="text-blue-200 text-sm">Data Integrity</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold">&lt;4hrs</div>
            <div class="text-blue-200 text-sm">Recovery Time</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-cyan-600 via-blue-700 to-indigo-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <h2 class="text-3xl md:text-4xl font-bold mb-6">Protect Your Business Data Today</h2>
        <p class="text-xl text-blue-100 mb-8">
          Don't wait for disaster to strike. Start protecting your critical business data with our comprehensive backup as a service solution powered by Veeam Backup Agent. Get enterprise-grade protection with 99.9% data integrity guaranteed.
        </p>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div class="flex items-center">
            <div class="bg-white/20 backdrop-blur-sm p-3 rounded-lg mr-4">
              <i class="bx bx-alarm text-2xl"></i>
            </div>
            <div>
              <div class="font-semibold">Quick Setup</div>
              <div class="text-sm text-blue-200">15-min setup</div>
            </div>
          </div>
          
          <div class="flex items-center">
            <div class="bg-white/20 backdrop-blur-sm p-3 rounded-lg mr-4">
              <i class="bx bx-headphone text-2xl"></i>
            </div>
            <div>
              <div class="font-semibold">Expert Support</div>
              <div class="text-sm text-blue-200">24/7 availability</div>
            </div>
          </div>
          
          <div class="flex items-center">
            <div class="bg-white/20 backdrop-blur-sm p-3 rounded-lg mr-4">
              <i class="bx bx-money-withdraw text-2xl"></i>
            </div>
            <div>
              <div class="font-semibold">Money Back</div>
              <div class="text-sm text-blue-200">30-day guarantee</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="space-y-4">
        <a routerLink="/contact" class="block bg-white text-cyan-600 font-bold py-4 px-6 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl">
          <i class="bx bx-shield-plus text-xl mr-2"></i>
          <span class="text-lg">Start Protection</span>
          <div class="text-sm font-normal text-gray-600 mt-1">Free 30-day trial</div>
        </a>
        
        <a routerLink="/contact" class="block bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-6 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300">
          <i class="bx bx-conversation text-xl mr-2"></i>
          <span class="text-lg">Get Custom Quote</span>
          <div class="text-sm font-normal text-blue-200 mt-1">Enterprise solutions</div>
        </a>
        
        <div class="bg-slate-800/30 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-700/30">
          <i class="bx bx-badge-check text-3xl mb-2"></i>
          <div class="font-semibold">99.9% Data Integrity Guaranteed</div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="py-24 bg-slate-950">
    <div class="container mx-auto px-4">
        <div class="text-center mb-16">
            <div class="inline-block bg-blue-500/10 border border-blue-500/40 text-blue-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
                Veeam Backup Agent
            </div>
            <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                Backup As A Service <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">Features</span>
            </h2>
            <p class="text-xl text-slate-300 max-w-3xl mx-auto">
                Complete data protection solutions with automated backup, disaster recovery planning, and rapid restoration capabilities powered by Veeam Backup Agent for maximum business continuity.
            </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- Automated Backup -->
            <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60 relative overflow-hidden group">
                <div class="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Core</div>
                <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i class="bx bx-data text-3xl text-white"></i>
                </div>
                <h3 class="text-xl font-bold text-white mb-3">Automated Backup</h3>
                <p class="text-slate-300 mb-6">
                    Comprehensive automated backup solutions powered by Veeam Backup Agent with continuous data protection, incremental backups, and intelligent scheduling.
                </p>
                
                <div class="mb-6">
                    <ul class="space-y-2">
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-blue-400 mr-2"></i> Continuous data protection</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-blue-400 mr-2"></i> Automated daily backups</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-blue-400 mr-2"></i> Incremental backup technology</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-blue-400 mr-2"></i> Intelligent deduplication</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Frequency</span>
                        <span class="font-semibold text-white">Every 15 min</span>
                    </div>
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Reduction</span>
                        <span class="font-semibold text-white">Up to 95%</span>
                    </div>
                </div>
            </div>

            <!-- Cloud Backup -->
            <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60 relative overflow-hidden group">
                <div class="absolute top-0 right-0 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Cloud</div>
                <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i class="bx bx-cloud-rain text-3xl text-white"></i>
                </div>
                <h3 class="text-xl font-bold text-white mb-3">Cloud Backup</h3>
                <p class="text-slate-300 mb-6">
                    Secure cloud backup with multi-region replication, unlimited scalability, and enterprise-grade security features powered by Veeam Backup Agent.
                </p>
                
                <div class="mb-6">
                    <ul class="space-y-2">
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-cyan-400 mr-2"></i> Multi-region replication</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-cyan-400 mr-2"></i> Unlimited storage capacity</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-cyan-400 mr-2"></i> AES-256 encryption</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-cyan-400 mr-2"></i> Geo-redundant storage</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Durability</span>
                        <span class="font-semibold text-white">99.999999999%</span>
                    </div>
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Availability</span>
                        <span class="font-semibold text-white">99.99%</span>
                    </div>
                </div>
            </div>

            <!-- Disaster Recovery -->
            <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60 relative overflow-hidden group">
                <div class="absolute top-0 right-0 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Recovery</div>
                <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i class="bx bx-receipt text-3xl text-white"></i>
                </div>
                <h3 class="text-xl font-bold text-white mb-3">Disaster Recovery</h3>
                <p class="text-slate-300 mb-6">
                    Complete disaster recovery solutions with business continuity planning, rapid restoration, and minimal downtime powered by Veeam Backup Agent.
                </p>
                
                <div class="mb-6">
                    <ul class="space-y-2">
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-indigo-400 mr-2"></i> Business continuity planning</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-indigo-400 mr-2"></i> Rapid system restoration</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-indigo-400 mr-2"></i> Point-in-time recovery</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-indigo-400 mr-2"></i> Bare metal restore</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">RTO</span>
                        <span class="font-semibold text-white">&lt; 4 hours</span>
                    </div>
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">RPO</span>
                        <span class="font-semibold text-white">&lt; 1 hour</span>
                    </div>
                </div>
            </div>

            <!-- Database Backup -->
            <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60 relative overflow-hidden group">
                <div class="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Database</div>
                <div class="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i class="bx bx-data text-3xl text-white"></i>
                </div>
                <h3 class="text-xl font-bold text-white mb-3">Database Backup</h3>
                <p class="text-slate-300 mb-6">
                    Specialized database backup solutions for SQL Server, Oracle, MySQL, PostgreSQL with transaction log protection powered by Veeam Backup Agent.
                </p>
                
                <div class="mb-6">
                    <ul class="space-y-2">
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-orange-400 mr-2"></i> Transaction log backup</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-orange-400 mr-2"></i> Multi-database support</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-orange-400 mr-2"></i> Automated consistency checks</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-orange-400 mr-2"></i> Hot backup capability</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Supported DBs</span>
                        <span class="font-semibold text-white">20+ platforms</span>
                    </div>
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Log Frequency</span>
                        <span class="font-semibold text-white">Every 5 min</span>
                    </div>
                </div>
            </div>

            <!-- VM Backup -->
            <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60 relative overflow-hidden group">
                <div class="absolute top-0 right-0 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Virtual</div>
                <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i class="bx bx-server text-3xl text-white"></i>
                </div>
                <h3 class="text-xl font-bold text-white mb-3">VM Backup</h3>
                <p class="text-slate-300 mb-6">
                    Advanced virtual machine backup with agentless protection, instant recovery, and seamless hypervisor integration powered by Veeam Backup Agent.
                </p>
                
                <div class="mb-6">
                    <ul class="space-y-2">
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-purple-400 mr-2"></i> Agentless VM backup</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-purple-400 mr-2"></i> VMware & Hyper-V support</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-purple-400 mr-2"></i> Instant VM recovery</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-purple-400 mr-2"></i> Changed block tracking</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Recovery Time</span>
                        <span class="font-semibold text-white">&lt; 30 seconds</span>
                    </div>
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Platforms</span>
                        <span class="font-semibold text-white">All major</span>
                    </div>
                </div>
            </div>

            <!-- 24/7 Monitoring -->
            <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-700/60 relative overflow-hidden group">
                <div class="absolute top-0 right-0 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">Support</div>
                <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i class="bx bx-group text-3xl text-white"></i>
                </div>
                <h3 class="text-xl font-bold text-white mb-3">24/7 Monitoring</h3>
                <p class="text-slate-300 mb-6">
                    Round-the-clock backup monitoring with proactive alerts, health checks, and expert technical support for your Veeam Backup Agent implementation.
                </p>
                
                <div class="mb-6">
                    <ul class="space-y-2">
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-green-400 mr-2"></i> 24/7 monitoring & alerts</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-green-400 mr-2"></i> Proactive issue detection</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-green-400 mr-2"></i> Health status reporting</li>
                        <li class="flex items-center text-slate-200 text-sm"><i class="bx bx-check-circle text-green-400 mr-2"></i> Expert technical support</li>
                    </ul>
                </div>
                
                <div class="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/60">
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Response Time</span>
                        <span class="font-semibold text-white">&lt; 15 minutes</span>
                    </div>
                    <div>
                        <span class="block text-xs text-slate-400 uppercase tracking-wider">Availability</span>
                        <span class="font-semibold text-white">24/7/365</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>
